import React, { useLayoutEffect, useReducer, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BsFacebook, BsInstagram, BsWhatsapp } from 'react-icons/bs';
import { MapPin, Phone, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher/LanguageSwitcher';
import settingsService from '../services/settings.service';
import newsletterService from '../services/newsletter.service';
import { hasPublicRichText, getError } from '../utils';
import { isAdminPath } from '../routes/paths';
import { toast } from 'react-toastify';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_SUCCESS':
      return { ...state, settings: action.payload };
    default:
      return state;
  }
};

export default function Footerr() {
  const { t } = useTranslation();
  const location = useLocation();
  const [{ settings }, dispatch] = useReducer(reducer, { settings: {} });
  const [logoURL, setLogoURL] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useLayoutEffect(() => {
    settingsService
      .getPublicSettings()
      .then(data => {
        setLogoURL(data.image?.fileLink || '');
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      })
      .catch(() => {});
  }, []);

  if (isAdminPath(location.pathname)) return null;

  // Pages that render a fixed bottom-0 checkout bar — footer needs breathing room
  const hasStickyCheckout = /^\/(comprar|tablero)\/[^/]+/.test(location.pathname);

  const showContact = hasPublicRichText(settings.contactPageHtml);
  const showTerms = hasPublicRichText(settings.termsPageHtml);

  const socialLinks = [
    settings.facebookUrl && {
      href: settings.facebookUrl,
      icon: <BsFacebook size={18} />,
      label: 'Facebook',
    },
    settings.instagramUrl && {
      href: settings.instagramUrl,
      icon: <BsInstagram size={18} />,
      label: 'Instagram',
    },
    settings.whatsappUrl && {
      href: `https://api.whatsapp.com/send?phone=${settings.whatsappNumber || ''}`,
      icon: <BsWhatsapp size={18} />,
      label: 'WhatsApp',
    },
  ].filter(Boolean);

  const handleSubscribe = async e => {
    e.preventDefault();
    if (!email || subscribing) return;
    setSubscribing(true);
    try {
      await newsletterService.subscribe(email);
      setSubmitted(true);
      setEmail('');
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer
      style={{
        backgroundColor: 'var(--primaryColor)',
        color: 'var(--navFontColor)',
        boxShadow: 'inset 0 4px 24px rgba(0,0,0,0.12)',
      }}
    >
      <div
        className={`mx-auto px-6 pt-10 ${hasStickyCheckout ? 'pb-24 lg:pb-6' : 'pb-6'}`}
        style={{ maxWidth: '1280px' }}
      >
        {/* ── 3-column grid ── */}
        <div className='grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3'>
          {/* ── Col 1: Brand info ── */}
          <div className='flex flex-col gap-4'>
            {logoURL ? (
              <img
                src={logoURL}
                alt={settings.companyName}
                style={{ height: '56px', width: 'auto', objectFit: 'contain' }}
                className='self-start'
              />
            ) : (
              <span className='text-xl font-bold' style={{ color: 'var(--navFontColor)' }}>
                {settings.companyName}
              </span>
            )}

            <div className='space-y-1'>
              <p className='text-base font-semibold' style={{ color: 'var(--navFontColor)' }}>
                {settings.companyName}
              </p>
              {settings.companysubName && (
                <p className='text-sm opacity-75'>{settings.companysubName}</p>
              )}
            </div>

            <address className='not-italic space-y-1.5 text-sm opacity-80'>
              {settings.companyAddress && (
                <p className='flex items-start gap-2'>
                  <MapPin size={14} className='mt-0.5 shrink-0 opacity-70' />
                  <span>
                    {settings.companyAddress}
                    {settings.companyCity ? `, ${settings.companyCity}` : ''}
                  </span>
                </p>
              )}
              {settings.companyPhone && (
                <p className='flex items-center gap-2'>
                  <Phone size={14} className='shrink-0 opacity-70' />
                  <span>{settings.companyPhone}</span>
                </p>
              )}
            </address>

            {/* Social icons */}
            {socialLinks.length > 0 && (
              <div className='flex gap-3 pt-1'>
                {socialLinks.map(({ href, icon, label }) => (
                  <a
                    key={label}
                    href={href}
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-label={label}
                    className='opacity-80 hover:opacity-100 transition-opacity'
                    style={{ color: 'var(--navFontColor)' }}
                  >
                    {icon}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* ── Col 2: Navigation links ── */}
          <div>
            <h6
              className='mb-4 text-sm font-semibold uppercase tracking-wider opacity-60'
              style={{ color: 'var(--navFontColor)' }}
            >
              {t('footer.aboutUs')}
            </h6>
            <ul className='space-y-2.5 text-sm'>
              <li>
                <Link
                  to='/'
                  className='opacity-75 hover:opacity-100 transition-opacity'
                  style={{ color: 'var(--navFontColor)' }}
                >
                  {t('nav.home')}
                </Link>
              </li>
              {showContact && (
                <li>
                  <Link
                    to='/contact'
                    className='opacity-75 hover:opacity-100 transition-opacity'
                    style={{ color: 'var(--navFontColor)' }}
                  >
                    {t('footer.contactUs')}
                  </Link>
                </li>
              )}
              <li>
                <Link
                  to='/faq'
                  className='opacity-75 hover:opacity-100 transition-opacity'
                  style={{ color: 'var(--navFontColor)' }}
                >
                  {t('nav.faq')}
                </Link>
              </li>
              {showTerms && (
                <li>
                  <Link
                    to='/bases'
                    className='opacity-75 hover:opacity-100 transition-opacity'
                    style={{ color: 'var(--navFontColor)' }}
                  >
                    {t('footer.termsAndConditions')}
                  </Link>
                </li>
              )}
              <li>
                <Link
                  to='/privacidad'
                  className='opacity-75 hover:opacity-100 transition-opacity'
                  style={{ color: 'var(--navFontColor)' }}
                >
                  {t('footer.privacyPolicy')}
                </Link>
              </li>
            </ul>
          </div>

          {/* ── Col 3: Newsletter ── */}
          <div>
            <h6
              className='mb-1 text-sm font-semibold uppercase tracking-wider opacity-60'
              style={{ color: 'var(--navFontColor)' }}
            >
              Newsletter
            </h6>
            <p className='mb-4 text-sm opacity-70'>Recibí novedades y promociones.</p>

            {submitted ? (
              <p className='flex items-center gap-2 text-sm font-medium opacity-90'>
                <Mail size={16} />
                ¡Gracias por suscribirte!
              </p>
            ) : (
              <form onSubmit={handleSubscribe} className='flex overflow-hidden rounded-lg'>
                <input
                  type='email'
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder='Tu email'
                  required
                  className='flex-1 bg-white/95 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 outline-none focus:bg-white'
                  style={{ minWidth: 0 }}
                />
                <button
                  type='submit'
                  disabled={subscribing}
                  className='shrink-0 px-4 py-2 text-xs font-bold uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-60'
                  style={{
                    backgroundColor: 'rgba(0,0,0,0.25)',
                    color: 'var(--navFontColor)',
                  }}
                >
                  {subscribing ? '...' : 'Suscribir'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div
          className='mt-10 flex flex-col items-center gap-3 border-t pt-5'
          style={{ borderColor: 'rgba(255,255,255,0.2)' }}
        >
          <LanguageSwitcher />

          <div
            className='flex flex-col items-center gap-1 text-center text-xs opacity-50'
            style={{ color: 'var(--navFontColor)' }}
          >
            <p>
              &copy; {new Date().getFullYear()} {settings.companyName}. Todos los derechos
              reservados.
            </p>
            <p>
              Powered by{' '}
              <a
                href='https://gotasoft.io'
                target='_blank'
                rel='noopener noreferrer'
                className='font-semibold hover:opacity-80 transition-opacity'
                style={{ color: 'var(--navFontColor)' }}
              >
                GotaSoft
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
